import { html, css, LitElement } from 'lit-element';
import { t, Localized } from './i18n.js';

// The Gemini endpoints currently available, for reference when setting the
// AI Summary Model in Configuration (admin_ai_config.js). Only text
// endpoints return the plain-text/JSON response generateAiSummary
// (functions/ai.js) expects from model.generateContent() -- image,
// live-streaming, and text-to-speech endpoints are listed for completeness
// but aren't a fit for that call.
//
// Endpoint names/availability are controlled by Google and can change
// without notice -- especially "preview" endpoints. Keep this list in sync
// with whatever Google currently documents; there's no API to fetch it
// (see functions/ai.js's resolveGeminiModel comment).
const MODELS = [
    { name: 'Gemini 3.7 Flash', endpoint: 'gemini-3.7-flash', type: 'textFlash', suitable: true, note: 'fastDefault' },
    { name: 'Gemini 3.6 Flash', endpoint: 'gemini-3.6-flash', type: 'textFlash', suitable: true, note: 'previousFlash' },
    { name: 'Gemini 3.5 Flash', endpoint: 'gemini-3.5-flash', type: 'textFlash', suitable: true, note: 'previousFlash' },
    { name: 'Gemini 3.5 Flash-Lite', endpoint: 'gemini-3.5-flash-lite', type: 'textFlashLite', suitable: true, note: 'lighter' },
    { name: 'Gemini 3.1 Flash-Lite', endpoint: 'gemini-3.1-flash-lite', type: 'textFlashLite', suitable: true, note: 'olderLight' },
    { name: 'Nano Banana 2', endpoint: 'gemini-3.1-flash-image', type: 'image', suitable: false, note: 'images' },
    { name: 'Nano Banana 2 Lite', endpoint: 'gemini-3.1-flash-lite-image', type: 'image', suitable: false, note: 'images' },
    { name: 'Nano Banana Pro', endpoint: 'gemini-3-pro-image', type: 'image', suitable: false, note: 'images' },
    { name: 'Gemini 3.1 Pro', endpoint: 'gemini-3.1-pro-preview', type: 'textProPreview', suitable: true, note: 'proPreview' },
    { name: 'Gemini 3 Flash', endpoint: 'gemini-3-flash-preview', type: 'textFlashPreview', suitable: true, note: 'preview' },
    { name: 'Gemini 3.5 Live Translate', endpoint: 'gemini-3.5-live-translate-preview', type: 'liveTranslate', suitable: false, note: 'streaming' },
    { name: 'Gemini 3.1 Flash Live', endpoint: 'gemini-3.1-flash-live-preview', type: 'live', suitable: false, note: 'streaming' },
    { name: 'Gemini 3.1 Flash TTS', endpoint: 'gemini-3.1-flash-tts-preview', type: 'tts', suitable: false, note: 'audio' },
    { name: 'Gemini Omni Flash', endpoint: 'gemini-omni-flash', type: 'omni', suitable: null, note: 'omni' },
];

// `type` and `note` are i18n keys under aiModels.type.* / aiModels.note.*.
export class IntrivityAiModelsGuide extends Localized(LitElement) {
    static get styles() {
        return css`
            :host {
                display: block;
                font-family: 'Muli', sans-serif;
                color: #222;
                max-width: 920px;
                margin: 0 auto;
                padding: 30px 20px 60px;
            }
            h1 {
                font-size: 1.8em;
                margin: 0 0 6px;
            }
            .lede {
                color: #666;
                max-width: 72ch;
                margin: 0 0 24px;
                line-height: 1.5;
            }
            .table-wrap {
                overflow-x: auto;
                border: 1px solid #e2e2e2;
                border-radius: 8px;
            }
            table {
                border-collapse: collapse;
                width: 100%;
                min-width: 760px;
                font-size: 0.88em;
            }
            thead th {
                text-align: left;
                font-size: 0.72em;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #6b6b6b;
                padding: 12px 14px;
                border-bottom: 1px solid #e2e2e2;
                background: #f7f7f7;
            }
            tbody td {
                padding: 11px 14px;
                border-bottom: 1px solid #eee;
                vertical-align: top;
                color: #555;
            }
            tbody tr:last-child td { border-bottom: none; }
            tbody td:first-child { color: #222; font-weight: 600; white-space: nowrap; }
            code {
                background: #f2f2f2;
                padding: 2px 5px;
                border-radius: 3px;
                font-size: 0.95em;
                white-space: nowrap;
            }
            .yes { color: rgb(30, 120, 128); font-weight: 700; }
            .no { color: #6b6b6b; }
            .unknown { color: #c98a2b; font-weight: 700; }
            .disclaimer {
                margin-top: 24px;
                font-size: 0.82em;
                color: #6b6b6b;
                line-height: 1.5;
                font-style: italic;
            }
        `;
    }

    render() {
        return html`
            <h1>${t('aiModels.title')}</h1>
            <p class="lede">
                ${t('aiModels.lede')}
            </p>
            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>${t('aiModels.colModel')}</th>
                            <th>${t('aiModels.colEndpoint')}</th>
                            <th>${t('aiModels.colType')}</th>
                            <th>${t('aiModels.colFits')}</th>
                            <th>${t('aiModels.colNotes')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${MODELS.map(m => html`
                            <tr>
                                <td>${m.name}</td>
                                <td><code>${m.endpoint}</code></td>
                                <td>${t(`aiModels.type.${m.type}`)}</td>
                                <td class=${m.suitable === true ? 'yes' : m.suitable === false ? 'no' : 'unknown'}>
                                    ${m.suitable === true ? t('common.yes') : m.suitable === false ? t('common.no') : t('aiModels.unconfirmed')}
                                </td>
                                <td>${t(`aiModels.note.${m.note}`)}</td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
            <p class="disclaimer">
                ${t('aiModels.disclaimer')}
            </p>
        `;
    }
}
if (!customElements.get('intrivity-ai-models-guide')) customElements.define('intrivity-ai-models-guide', IntrivityAiModelsGuide);
